import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { importProductsFromExcel } from "../../../slice/product/productSlice";
import { fetchGalleryImages } from "../../../slice/gallery/galleryImageSlice";
import api from "../../../api/axios";

/* ── small badge helper ── */
const Badge = ({ active, activeLabel, inactiveLabel, activeColor, inactiveBg = "var(--mly-surface-2)" }) => (
  <span
    style={{
      background:   active ? activeColor.bg   : inactiveBg,
      color:        active ? activeColor.text : "var(--mly-text-muted)",
      fontSize:     11,
      fontWeight:   700,
      padding:      "3px 10px",
      borderRadius: 20,
      border:       `1px solid ${active ? activeColor.border : "var(--mly-border)"}`,
      whiteSpace:   "nowrap",
    }}
  >
    {active ? activeLabel : inactiveLabel}
  </span>
);

/* ── brand palette for highlight chips / badges ── */
const BRAND = {
  forest: "var(--mly-heading)",
  pine:   "var(--mly-accent)",
  gold:   "var(--mly-accent)",
  honey:  "var(--mly-accent-2)",
  cream:  "var(--mly-surface-2)",
  sand:   "var(--mly-border)",
  ink:    "var(--mly-text)",
  stone:  "var(--mly-text-soft)",
};

/* ═══════════════════════════════════════════════════════════════
   TWO SALE CHANNELS, TWO INDEPENDENT STOCK POOLS

   One product can sell two ways at once — e.g. Byadgi chilli is
   milled to order from a bulk bin AND sits on the shelf in sealed
   pouches:

     • SHELF  (sellsPrepacked)   → packsOnHand, in packs
     • BULK   (sellsBulkMilled)  → the bin, in GRAMS on the wire
       (GET /admin/bulk/grains), shown here in kg.

   The two pools are INDEPENDENT. This screen never adds them
   together and never moves stock between them; there is no
   pack-from-bulk operation anywhere in this system.

   STOCK IS PER STORE NOW. `packsOnHand` on a product row counts only
   the acting store's shelf, and is 0 when the request named no store —
   which this screen's product query never does. So it is only read
   as a number for a store-bound account; for an estate-wide one the
   cell says "—" rather than reporting the whole catalogue as empty.
═══════════════════════════════════════════════════════════════ */
const LOW_STOCK_DEFAULT = 10;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/* Which channels does this product sell through? Two always-present
   booleans on the row — there is no legacy productType shape left. */
const channelsOf = (p) => ({
  bulk:  !!p?.sellsBulkMilled,
  shelf: !!p?.sellsPrepacked,
});

/* entryBulkKg = this product's bulk bin, in kg (from /admin/bulk/grains).
   `estateWide` says the caller is not pinned to a store, in which case the
   row's packsOnHand was counted against no store at all and means nothing.

   TODO(cutover): server does not return a per-pack breakdown on the list row
   (packCount is a count of SKUs, not stock), so the expandable pack list has
   no source here — it needs GET /stock/balances?storeId=&productId=, one
   request per row, which this screen does not make. */
const deriveStock = (p, entryBulkKg, estateWide, threshold) => {
  const chan = channelsOf(p);
  /* a product that does not sell prepacked has no shelf count to report —
     0 packs is not "out of stock" for a bin product */
  const total = estateWide || !chan.shelf ? null : num(p?.packsOnHand);
  const bulk  = entryBulkKg ?? null;

  const low = total !== null && total <= threshold;

  return { total, source: "packs", packs: [], low, bulk, channels: chan };
};

/* ── channel chip: "Shelf" / "Bulk" / "Shelf + Bulk" ── */
const ChannelChip = ({ channels }) => {
  const { shelf, bulk } = channels;
  const label = shelf && bulk ? "Shelf + Bulk" : shelf ? "Shelf" : bulk ? "Bulk" : "—";

  if (label === "—") {
    return <span style={{ color: "var(--mly-text-muted)", fontSize: 12 }}>—</span>;
  }

  const tone =
    shelf && bulk
      ? { bg: BRAND.cream,  fg: BRAND.forest, bd: BRAND.gold }
      : shelf
      ? { bg: "#f0fdf4",    fg: "#16a34a",    bd: "#bbf7d0" }
      : { bg: "#fff7ed",    fg: "#b45309",    bd: "#fed7aa" };

  return (
    <span
      title={
        shelf && bulk
          ? "Sold as sealed packs AND milled to order from the bulk bin — two independent stock pools"
          : shelf
          ? "Sold as sealed packs on the customer shelf"
          : "Sold loose by weight through custom milling only"
      }
      style={{
        background: tone.bg, color: tone.fg, border: `1px solid ${tone.bd}`,
        fontSize: 11, fontWeight: 700, padding: "3px 10px",
        borderRadius: 20, whiteSpace: "nowrap", display: "inline-block",
      }}
    >
      {label}
    </span>
  );
};

/* ── Stock cell: shelf total + gold Low chip + per-pack breakdown,
      with the bulk bin shown as its own separate line ── */
const StockCell = ({ info, expanded, onToggle }) => {
  const { total, packs, low, source, bulk, channels } = info;

  const showShelf = channels.shelf || total !== null;
  const out       = total === 0;
  const color     = out ? "#ef4444" : low ? "#8A6516" : "#16a34a";
  const bg        = out ? "#fff5f5" : low ? "#FDF6E3" : "#f0fdf4";
  const border    = out ? "#fecaca" : low ? "#C9952A"  : "#bbf7d0";
  const summary   = packs.map((v) => `${v.label}: ${v.stock}`).join(" · ");

  /* the shelf number is packs only now, so the bin is never a repeat of it
     and shows for a bulk-only product too */
  const bulkIsSeparate = bulk !== null && channels.bulk;

  if (total === null && bulk === null) {
    return <span style={{ color: "var(--mly-text-muted)", fontSize: 12 }}>—</span>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
      {showShelf && total !== null && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span
            title={summary || (source === "entry" ? "Stock entry balance" : undefined)}
            style={{
              background: bg, color, border: `1px solid ${border}`,
              fontWeight: 700, fontSize: 13, padding: "3px 12px",
              borderRadius: 20, whiteSpace: "nowrap",
            }}
          >
            {out ? "Out of Stock" : total}
          </span>

          {low && !out && (
            <span style={{
              background: BRAND.gold, color: BRAND.cream,
              fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
              padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
            }}>
              Low
            </span>
          )}

          {packs.length > 0 && (
            <button
              type="button"
              onClick={onToggle}
              title={summary}
              style={{
                background: "transparent", border: "none", padding: 0,
                color: BRAND.forest, fontSize: 10.5, fontWeight: 700,
                cursor: "pointer", textDecoration: "underline",
                textUnderlineOffset: 2, whiteSpace: "nowrap",
              }}
            >
              {expanded ? "hide packs" : `${packs.length} pack${packs.length > 1 ? "s" : ""}`}
            </button>
          )}
        </div>
      )}

      {packs.length > 0 && !expanded && (
        <span
          title={summary}
          style={{
            fontSize: 10.5, color: "var(--mly-text-muted)", maxWidth: 170,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {summary}
        </span>
      )}

      {packs.length > 0 && expanded && (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {packs.map((v, i) => (
            <span
              key={v.sku || v.label || i}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "#FDF6E3", border: "1px solid #E8DFC8",
                borderRadius: 8, padding: "2px 8px",
                fontSize: 10.5, color: "#15281D", whiteSpace: "nowrap",
              }}
            >
              <span style={{ fontWeight: 700 }}>{v.label}</span>
              <span style={{ color: v.stock === 0 ? "#ef4444" : v.stock <= 5 ? "#8A6516" : "#16a34a", fontWeight: 800 }}>
                {v.stock}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* the bulk bin — its own pool, in the mill's unit (kg), never
          added to the pack numbers above */}
      {bulkIsSeparate && (
        <span
          title="Bulk bin for custom milling — a separate pool from the packs above"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "#fff7ed", border: "1px solid #fed7aa",
            borderRadius: 8, padding: "2px 8px",
            fontSize: 10.5, color: "#b45309", whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontWeight: 700 }}>bulk</span>
          <span style={{ fontWeight: 800 }}>{bulk} kg</span>
        </span>
      )}

      {packs.length === 0 && source === "entry" && (
        <span style={{ fontSize: 10, color: "var(--mly-text-muted)" }}>
          {channels.bulk && !channels.shelf ? "bulk bin (kg)" : "base stock"}
        </span>
      )}
    </div>
  );
};

/* Highlight filter chips.
   FILTERED SERVER-SIDE: each chip's `param` is sent as
   GET /admin/catalog/products?highlight=… so the chip matches across all 515
   products, not just the page in the browser. */
const HIGHLIGHT_CHIPS = [
  { key: "all",        label: "All",         param: "" },
  { key: "wow",        label: "WOW",         param: "wow" },
  { key: "gi",         label: "GI Tagged",   param: "gi" },
  { key: "popular",    label: "Popular",     param: "popular" },
  { key: "bestSeller", label: "Best Seller", param: "bestSeller" },
  { key: "millable",   label: "Millable",    param: "millable" },
];

const PAGE_SIZES = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 350;

const IteamMaster = () => {
  const tableRef     = useRef(null);
  const navigate     = useNavigate();
  const dispatch     = useDispatch();
  const fileInputRef = useRef(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [excelData,       setExcelData]       = useState(null);
  const [importing,       setImporting]       = useState(false);
  const [importProgress,  setImportProgress]  = useState(0);

  /* search: `searchInput` is what the user is typing, `search` is the
     debounced value that actually goes to the API */
  const [searchInput,     setSearchInput]     = useState("");
  const [search,          setSearch]          = useState("");
  const [highlightFilter, setHighlightFilter] = useState("all");
  const [categoryFilter,  setCategoryFilter]  = useState("");
  const [statusFilter,    setStatusFilter]    = useState("");
  const [page,            setPage]            = useState(1);
  const [limit,           setLimit]           = useState(25);

  const [categories,      setCategories]      = useState([]);
  const [chipCounts,      setChipCounts]      = useState({});
  const [expandedStock,   setExpandedStock]   = useState({});
  /* TODO(cutover): server does not return StoreSetting.lowStockThreshold —
     GET /admin/store-settings has no such field, so the default stands. */
  const lowThreshold = LOW_STOCK_DEFAULT;
  /* an estate-wide caller's rows carry no store scope (see the header note) */
  const [estateWide,      setEstateWide]      = useState(false);

  /* The table reads GET /admin/catalog/products directly rather than through
     the product slice: the chip counts below need their own probes and the
     search/paging state is local to this screen. */
  const [products,   setProducts]   = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* The bulk bins, read straight from GET /admin/bulk/grains. */
  const [stockEntries, setStockEntries] = useState([]);

  /* the very first paint gets the full-page spinner; after that a
     re-query must NEVER unmount the search box (it would steal focus
     mid-typing), so later loads only dim the table */
  const [booted, setBooted] = useState(false);

  const rows = Array.isArray(products) ? products : [];

  /* Bulk bins — a row per bulk product from GET /admin/bulk/grains, where
     `id` IS the product id and onHandGrams is in GRAMS (and null unless the
     request named a store). Kept in kg here, which is how the cell reads. */
  const stockMap = React.useMemo(() => {
    const map = {};
    (stockEntries || []).forEach((grain) => {
      const pid = grain?.id;
      if (!pid) return;
      const grams = Number(grain.onHandGrams);
      if (!Number.isFinite(grams)) return;
      map[pid] = (map[pid] || 0) + grams / 1000;
    });
    return map;
  }, [stockEntries]);

  /* Who is asking. `canEditGlobal` is the server's own answer, not a guess
     off the row count: an estate-wide caller sends no ?storeId= through the
     shared products thunk, so its packsOnHand is 0 for every row. */
  useEffect(() => {
    let alive = true;
    api.get("/admin/store-settings/overview")
      .then((res) => {
        if (alive) setEstateWide(!!res.data?.meta?.canEditGlobal);
      })
      .catch(() => { /* leave the shelf numbers as sent */ });
    return () => { alive = false; };
  }, []);

  /* category options for the server-side category filter.
     limit is clamped to 100 server-side, so 100 is what it can answer. */
  useEffect(() => {
    let alive = true;
    api.get("/admin/catalog/categories?limit=100")
      .then((res) => {
        const list = res.data?.data ?? [];
        if (alive && Array.isArray(list)) setCategories(list);
      })
      .catch(() => { if (alive) setCategories([]); });
    return () => { alive = false; };
  }, []);

  const toggleStockPacks = (id) =>
    setExpandedStock((prev) => ({ ...prev, [id]: !prev[id] }));

  /* ── gallery image lookup: "0004" -> "0004-num1.png" (fallback only) ── */
  const { images: galleryImages } = useSelector((s) => s.galleryImage);
  const imageLookup = React.useMemo(() => {
    const map = {};
    (galleryImages || []).forEach((g) => {
      if (!g.image) return;
      const bareName = g.image.split("/").pop();           // "products/0004-num1.png" -> "0004-num1.png"
      const base = bareName.split("-")[0].split(".")[0];    // "0004"
      if (!map[base]) map[base] = bareName;                  // "0004-num1.png"
    });
    return map;
  }, [galleryImages]);

  /* ═══════════════════════════════════════════════════════════════
     SEARCH — DEBOUNCE
     The box used to filter `products` in memory, but only the first
     100 of 515 products were ever loaded, so "13 grain multigrain" or
     "ghee" matched nothing even though the API finds them. The query
     now goes to the server after ~350ms of quiet.
  ═══════════════════════════════════════════════════════════════ */
  useEffect(() => {
    const next = searchInput.trim();
    if (next === search) return undefined;
    const t = setTimeout(() => {
      setPage(1);                       // a new query always starts at page 1
      setSearch(next);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, search]);

  const highlightParam =
    HIGHLIGHT_CHIPS.find((c) => c.key === highlightFilter)?.param ?? "";

  /* ── the one query that feeds the table ──
     GET /admin/catalog/products answers { data, pagination } — the inner
     `data` is the payload's own key, not a leftover Node envelope. */
  const pageSeq = useRef(0);
  const loadPage = React.useCallback(async () => {
    const seq = ++pageSeq.current;
    setLoading(true);
    const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search)              qs.set("search",    search);
    if (highlightParam)      qs.set("highlight", highlightParam);
    if (categoryFilter)      qs.set("category",  categoryFilter);
    if (statusFilter !== "") qs.set("status",    statusFilter);
    try {
      const res = await api.get(`/admin/catalog/products?${qs.toString()}`);
      if (seq !== pageSeq.current) return;
      setProducts(Array.isArray(res.data?.data) ? res.data.data : []);
      setPagination(res.data?.pagination ?? null);
      setError("");
    } catch (e) {
      if (seq !== pageSeq.current) return;
      /* a real HTTP status with { code, message } — no status field in the body */
      setProducts([]);
      setError(e.response?.data?.message || "Failed to fetch products");
    } finally {
      if (seq === pageSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, highlightParam, categoryFilter, statusFilter]);

  useEffect(() => {
    /* loadPage never rejects (failures land in `error`), so this flips
       `booted` on the first settle either way */
    loadPage().then(() => setBooted(true), () => setBooted(true));
  }, [loadPage]);

  /* supporting data — best effort, only used as a fallback */
  useEffect(() => {
    let alive = true;
    /* { scope, grains, limit, offset } — a bare list of the bulk bins */
    api.get("/admin/bulk/grains?limit=500")
      .then((res) => {
        if (alive) setStockEntries(Array.isArray(res.data?.grains) ? res.data.grains : []);
      })
      .catch(() => { if (alive) setStockEntries([]); });
    return () => { alive = false; };
  }, []);

  /* ── The gallery, only if this page actually needs it ──
     Rows carry their own image, and the gallery is the fallback for the few
     that do not: a match on product code against the uploads folder. Fetching
     a thousand gallery rows on every visit to this screen, to rescue a handful
     of products that may not even be on the page, is most of what the screen
     cost to open. Asked for once, the first time a row turns up without one. */
  const galleryAsked = useRef(false);
  useEffect(() => {
    if (galleryAsked.current) return;
    if (!rows.some((p) => !p?.image)) return;
    galleryAsked.current = true;
    dispatch(fetchGalleryImages({ page: 1, limit: 1000 }));
  }, [rows, dispatch]);

  /* ── chip counts: one cheap limit=1 probe per chip, so the numbers
        count the WHOLE catalog under the current search, not the page ── */
  const countSeq = useRef(0);
  useEffect(() => {
    const seq = ++countSeq.current;
    const qs = new URLSearchParams();
    if (search)         qs.set("search", search);
    if (categoryFilter) qs.set("category", categoryFilter);
    if (statusFilter !== "") qs.set("status", statusFilter);

    /* ONE request for all six chips. This used to be six GET /products calls
       with limit=1, each read only for pagination.total -- six round trips and
       six counts of the whole catalogue, re-fired on every debounced keystroke,
       while the table the operator is actually reading waited its turn.
       /highlight-counts answers them in a single pass under the same filters,
       so a chip and the list it opens still cannot disagree. */
    api.get(`/admin/catalog/products/highlight-counts?${qs.toString()}`)
      .then((res) => {
        if (seq !== countSeq.current) return;    // a newer search won
        const c = res.data?.counts || {};
        setChipCounts({
          all: Number(c.all) || 0,
          wow: Number(c.wow) || 0,
          gi: Number(c.gi) || 0,
          popular: Number(c.popular) || 0,
          bestSeller: Number(c.bestseller) || 0,
          millable: Number(c.millable) || 0,
        });
      })
      .catch(() => { /* the chips keep whatever numbers they had */ });
  }, [search, categoryFilter, statusFilter]);

  /* ── handlers ── */
  /* The three toggles take NO body — each flips the stored value and answers
     the new one, so the row is re-read rather than guessed at. */
  const toggleFlag = async (id, path) => {
    try {
      await api.patch(`/admin/catalog/products/${id}/${path}`);
    } catch (e) {
      setError(e.response?.data?.message || "Could not update that product");
    }
    await loadPage();
  };
  const handleToggleStatus      = (id) => toggleFlag(id, "status");
  const handleToggleBestSelling = (id) => toggleFlag(id, "toggle-best-selling");
  const handleTogglePopular     = (id) => toggleFlag(id, "toggle-popular");
  /* DELETE no longer deletes: it deactivates the product and all its packs,
     so the row STAYS in the list with Status off. Superadmin only — a branch
     account gets a 403. */
  const handleDelete = async (id) => {
    if (!window.confirm("Retire this product? It stops selling everywhere, keeps its history, and stays in this list switched off.")) return;
    try {
      await api.delete(`/admin/catalog/products/${id}`);
    } catch (e) {
      /* 403 for a branch account, 404 for a product outside its scope */
      setError(e.response?.data?.message || "Could not retire that product");
    }
    await loadPage();
  };
  const handleHighlightChange = (key) => { setPage(1); setHighlightFilter(key); };
  const goToPage = (next) => { setPage(next); };

  /* ── Excel Import Handlers ── */
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data      = new Uint8Array(e.target.result);
      const workbook  = XLSX.read(data, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData  = XLSX.utils.sheet_to_json(firstSheet);
      setExcelData(jsonData);
      setShowImportModal(true);
    };
    reader.readAsArrayBuffer(file);
    event.target.value = "";
  };

  const handleImport = async () => {
    if (!excelData || excelData.length === 0) return;
    setImporting(true);
    setImportProgress(0);

    try {
      // Large batches so rows of the same productCode (variant families)
      // always travel in one request — the server groups them per call.
      const batchSize  = 500;
      let allResults   = { successful: [], failed: [], duplicates: [] };

      for (let i = 0; i < excelData.length; i += batchSize) {
        const batch = excelData.slice(i, i + batchSize);
        const res   = await dispatch(importProductsFromExcel(batch)).unwrap();

        allResults.successful.push(...(res.data?.successful ?? []));
        allResults.failed.push(...(res.data?.failed       ?? []));
        allResults.duplicates.push(...(res.data?.duplicates  ?? []));

        setImportProgress(((i + batch.length) / excelData.length) * 100);
      }

      await loadPage();                                       // destroys the table for us
      await api.get("/admin/bulk/grains?limit=500")
        .then((res) => setStockEntries(Array.isArray(res.data?.grains) ? res.data.grains : []))
        .catch(() => { /* the bulk column keeps what it has */ });

      const { successful, failed, duplicates } = allResults;
      let msg = `Imported: ${successful.length}`;
      if (duplicates.length) msg += `\nDuplicates skipped: ${duplicates.length}`;
      if (failed.length) {
        msg += `\nFailed: ${failed.length}`;
        failed.forEach((f) => { msg += `\n  - ${f.name}: ${f.error}`; });
      }
      alert(msg);

      setShowImportModal(false);
      setExcelData(null);
    } catch (err) {
      console.error("Import error:", err);
      alert("Import failed: " + (err?.message ?? "Unknown error"));
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const downloadTemplate = () => {
    /* v2 sheet: ONE ROW PER VARIANT — rows sharing a productCode become one
       product with multiple pack sizes. A product sold loose from the bulk
       bin (millable/roastable) uses a single row; its stock/price is per kg. */
    const template = [
      {
        name:               "Sharbati Wheat Flour",
        subCategory:        "Fresh Milling",
        productCode:        "300",
        variantLabel:       "1 kg",
        variantSku:         "300-1kg",
        mrp:                90,
        sellingPrice:       85,
        openingStock:       100,
        taxStructure:       "NOTAX",
        maxOffer:           10,
        isMillable:         "Yes",
        isRoastable:        "No",
        freshMilling:       "Yes",
        freshMasala:        "No",
        imageFileName:      "300.png",
        description:        "Freshly milled sharbati wheat flour",
      },
      {
        name:               "Sharbati Wheat Flour",
        subCategory:        "Fresh Milling",
        productCode:        "300",
        variantLabel:       "500 grams",
        variantSku:         "",            // blank → auto "300-500grams"
        mrp:                50,
        sellingPrice:       46,
        openingStock:       80,
      },
      {
        name:               "Whole Byadgi Chilli",
        subCategory:        "Fresh Milling",
        productCode:        "701",
        variantLabel:       "",            // bulk only: no pack variants
        mrp:                400,           // per kg
        sellingPrice:       380,           // per kg
        openingStock:       50,            // kg in the bulk bin
        taxStructure:       "NOTAX",
        isMillable:         "Yes",
        isRoastable:        "Yes",
        freshMasala:        "Yes",
        imageFileName:      "701.png",
        description:        "Sun-dried byadgi chillies, custom milled to order",
      },
    ];

    const readme = [
      { Instructions: "ONE ROW PER VARIANT. Rows with the same productCode merge into one product with multiple pack sizes." },
      { Instructions: "variantLabel examples: '1 kg', '500 grams', '200 g'. Weight is parsed from the label automatically." },
      { Instructions: "variantSku left blank auto-generates as productCode-label (e.g. 300-1kg)." },
      { Instructions: "sellingPrice left blank defaults to the MRP. openingStock is packs on hand for that variant." },
      { Instructions: "Bulk bin: set isMillable=Yes on a single row with NO variantLabel — mrp/sellingPrice/openingStock are then per kg." },
      { Instructions: "A product can do both: give it pack rows AND isMillable=Yes. The pack stock and the bulk bin stay separate pools." },
      { Instructions: "IMAGES: upload photos first via Product > Image Upload, named productCode.png (e.g. 300.png). The import links them automatically. imageFileName column can override." },
      { Instructions: "subCategory and taxStructure must match existing names exactly (case-insensitive)." },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    ws["!cols"] = [
      { wch: 24 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 8 },  { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 10 },
      { wch: 10 }, { wch: 11 }, { wch: 12 }, { wch: 11 }, { wch: 14 },
      { wch: 40 },
    ];
    const wsReadme = XLSX.utils.json_to_sheet(readme);
    wsReadme["!cols"] = [{ wch: 120 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.utils.book_append_sheet(wb, wsReadme, "READ ME");
    XLSX.writeFile(wb, "milliyo_import_template.xlsx");
  };

  /* ── helpers ── */
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
  };

  const BASE_URL = process.env.REACT_APP_API_URL;

  /* ── Product image resolver ──
     1) real uploaded image, 2) gallery match on productCode, 3) placeholder ── */
  const resolveProductImageSrc = (p) => {
    /* one image string per row (the first by sort order), not an array */
    const raw = p.image;
    if (raw) return raw.startsWith("http") ? raw : `${BASE_URL}/uploads/${raw}`;

    const code = p.productCode?.trim();
    const base = code ? code.split("-")[0].split(".")[0] : null;
    const resolvedName = base ? imageLookup[base] : null;
    if (resolvedName) return `${BASE_URL}/uploads/products/${resolvedName}`;

    return "/assets/images/placeholder.png";
  };

  /* ── pagination maths ── */
  const total      = Number(pagination?.total) || 0;
  const totalPages = Number(pagination?.totalPages) || (total ? Math.ceil(total / limit) : 1);
  const firstRow   = total === 0 ? 0 : (page - 1) * limit + 1;
  const lastRow    = Math.min(page * limit, total);

  const pageWindow = React.useMemo(() => {
    const span = 5;
    let start = Math.max(1, page - Math.floor(span / 2));
    let end   = Math.min(totalPages, start + span - 1);
    start = Math.max(1, end - span + 1);
    const out = [];
    for (let i = start; i <= end; i += 1) out.push(i);
    return out;
  }, [page, totalPages]);

  /* ── render states — the FULL-PAGE spinner is only for the first load;
     re-querying must not unmount the search box ── */
  if (!booted) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const filterStyle = {
    fontSize: 13, padding: "7px 10px", borderRadius: 8,
    border: `1px solid ${BRAND.sand}`, background: "var(--mly-surface)", color: BRAND.ink,
    minWidth: 150,
  };

  const pageBtn = (active, disabled) => ({
    minWidth: 34, height: 32, padding: "0 10px", borderRadius: 8,
    border: `1px solid ${active ? BRAND.forest : BRAND.sand}`,
    background: active ? BRAND.forest : "var(--mly-surface)",
    color: active ? BRAND.cream : disabled ? "var(--mly-text-muted)" : BRAND.ink,
    fontWeight: 700, fontSize: 12.5,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
  });

  return (
    <div>
      <style>{`
        .import-modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex; align-items: center; justify-content: center;
          z-index: 1050;
        }
        .import-modal {
          background: var(--mly-surface); border-radius: 12px; padding: 24px;
          max-width: 90%; width: 600px; max-height: 80vh; overflow-y: auto;
        }
        .progress-bar-wrap { width: 100%; height: 8px; background: var(--mly-surface-3); border-radius: 4px; overflow: hidden; }
        .progress-fill     { height: 100%; background: ${BRAND.pine}; transition: width 0.3s ease; }
      `}</style>

      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>

          {/* ── header ── */}
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
            <div className="job-filter">
              <div className="faq-form">
                <input
                  className="form-control"
                  type="text"
                  placeholder="Search name or code…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="search-icon" aria-hidden="true"><path d="M10.6 2.4a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4Zm0 2.2a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm5.9 11.3 4.8 4.8a1.2 1.2 0 0 1-1.7 1.7l-4.8-4.8a9.6 9.6 0 0 0 1.7-1.7Z" /></svg>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                className="btn btn-success btn-sm"
                onClick={() => fileInputRef.current.click()}
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.1a9.9 9.9 0 1 0 0 19.8 9.9 9.9 0 0 0 0-19.8Zm0 4.3a1.2 1.2 0 0 1 1.2 1.2v5a1.2 1.2 0 0 1-2.4 0v-5A1.2 1.2 0 0 1 12 6.4Zm0 9a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6Z" /></svg>
                Import Excel
              </button>
              <button
                className="btn btn-info btn-sm"
                onClick={downloadTemplate}
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 15.6 5.2 8.8a1.2 1.2 0 0 1 1.7-1.7L12 12.2l5.1-5.1a1.2 1.2 0 0 1 1.7 1.7Z" /></svg>
                Download Template
              </button>
              <button
                className="btn btn-sm"
                onClick={() => navigate("/admin/products/stockentry")}
                title="Set or add stock per pack, and top up the bulk bin"
                style={{
                  background: BRAND.cream, color: BRAND.forest,
                  border: `1px solid ${BRAND.gold}`, fontWeight: 700,
                  display: "flex", alignItems: "center", gap: 5,
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9.9 2.1h4.2a2.9 2.9 0 0 1 2.9 2.9v1.1h3.2a1.1 1.1 0 0 1 0 2.2h-3.2V21a1.1 1.1 0 0 1-2.2 0V5a.7.7 0 0 0-.7-.7H9.9a.7.7 0 0 0-.7.7v16a1.1 1.1 0 0 1-2.2 0V8.3H3.8a1.1 1.1 0 0 1 0-2.2H7V5a2.9 2.9 0 0 1 2.9-2.9Z" /></svg>
                Manage Stock
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => navigate("/admin/products/add")}
              >
                + Add
              </button>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
            />
          </div>

          {/* ── body ── */}
          <div className="card-body">

            {/* ── highlight filter chips (server-side via ?highlight=) ── */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {HIGHLIGHT_CHIPS.map((chip) => {
                const active = highlightFilter === chip.key;
                const count  = chipCounts[chip.key];
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => handleHighlightChange(chip.key)}
                    style={{
                      background:   active ? BRAND.forest : BRAND.cream,
                      color:        active ? BRAND.cream  : BRAND.ink,
                      border:       `1px solid ${active ? BRAND.forest : BRAND.sand}`,
                      borderRadius: 20,
                      padding:      "5px 14px",
                      fontSize:     12,
                      fontWeight:   700,
                      cursor:       "pointer",
                      whiteSpace:   "nowrap",
                      display:      "inline-flex",
                      alignItems:   "center",
                      gap:          6,
                    }}
                  >
                    {chip.label}
                    {Number.isFinite(count) && (
                      <span
                        style={{
                          background:   active ? BRAND.gold : BRAND.sand,
                          color:        active ? BRAND.cream : BRAND.ink,
                          borderRadius: 10,
                          padding:      "1px 7px",
                          fontSize:     10,
                          fontWeight:   800,
                        }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* ── server-side filters + result summary ── */}
            <div style={{
              display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
              marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${BRAND.sand}`,
            }}>
              <select
                style={filterStyle}
                value={categoryFilter}
                onChange={(e) => { setPage(1); setCategoryFilter(e.target.value); }}
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              <select
                style={filterStyle}
                value={statusFilter}
                onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
              >
                <option value="">Any status</option>
                <option value="true">Active only</option>
                <option value="false">Inactive only</option>
              </select>

              <select
                style={{ ...filterStyle, minWidth: 110 }}
                value={limit}
                onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>

              <span style={{ fontSize: 12.5, color: BRAND.stone, marginLeft: "auto" }}>
                {loading
                  ? "Searching…"
                  : total === 0
                  ? "No matches"
                  : `Showing ${firstRow}–${lastRow} of ${total}`}
                {search && !loading && (
                  <span style={{ marginLeft: 6, color: BRAND.forest, fontWeight: 700 }}>
                    for “{search}”
                  </span>
                )}
              </span>

              {(search || categoryFilter || statusFilter || highlightFilter !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setPage(1);
                    setSearchInput(""); setSearch("");
                    setCategoryFilter(""); setStatusFilter(""); setHighlightFilter("all");
                  }}
                  style={{
                    background: "transparent", border: "none", padding: 0,
                    color: BRAND.forest, fontSize: 12, fontWeight: 700,
                    cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2,
                  }}
                >
                  clear filters
                </button>
              )}
            </div>

            {error && (
              <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>
            )}

            <div style={{ overflowX: "auto", opacity: loading ? 0.55 : 1, transition: "opacity .15s" }}>
              <table
                className="display table table-bordered"
                ref={tableRef}
                style={{ width: "100%" }}
              >
                <thead>
                  <tr>
                    <th style={{ minWidth: 60  }}>Si No</th>
                    <th style={{ minWidth: 80  }}>Image</th>
                    <th style={{ minWidth: 180 }}>Name</th>
                    <th style={{ minWidth: 180 }}>Category</th>
                    <th style={{ minWidth: 175 }}>Stock</th>
                    <th style={{ minWidth: 120 }}>Channel</th>
                    <th style={{ minWidth: 180 }}>Tax Structure</th>
                    <th style={{ minWidth: 120 }}>Millable</th>
                    <th style={{ minWidth: 120 }}>Roastable</th>
                    <th style={{ minWidth: 130 }}>Max Offer</th>
                    <th style={{ minWidth: 120 }}>Status</th>
                    <th style={{ minWidth: 150 }}>Best Selling</th>
                    <th style={{ minWidth: 150 }}>Popular Product</th>
                    <th style={{ minWidth: 140 }}>Created By</th>
                    <th style={{ minWidth: 140 }}>Created Date</th>
                    <th style={{ minWidth: 140 }}>Updated By</th>
                    <th style={{ minWidth: 140 }}>Updated Date</th>
                    <th style={{ minWidth: 160 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p, idx) => {
                    /* packsOnHand is the shelf; the grains row is the bin — separate */
                    const stockInfo = deriveStock(p, stockMap[p.id], estateWide, lowThreshold);
                    return (
                      <tr key={p.id}>
                        <td>{(page - 1) * limit + idx + 1}</td>
                        <td>
                          <img
                            src={resolveProductImageSrc(p)}
                            alt={p.name}
                            style={{ width: 50, height: 50, borderRadius: 8, objectFit: "cover" }}
                          />
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <span>{p.name}</span>
                            {p.isWowItem && (
                              <span style={{
                                background: BRAND.gold, color: BRAND.cream,
                                fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                                padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
                              }}>
                                WOW
                              </span>
                            )}
                            {p.isGiTagged && (
                              <span style={{
                                background: BRAND.forest, color: BRAND.cream,
                                fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                                padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
                              }}>
                                GI
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{p.categoryName ?? "—"}</td>
                        <td>
                          <StockCell
                            info={stockInfo}
                            expanded={!!expandedStock[p.id]}
                            onToggle={() => toggleStockPacks(p.id)}
                          />
                        </td>

                        {/* Channel */}
                        <td><ChannelChip channels={stockInfo.channels} /></td>

                        {/* Tax Structure */}
                        <td>
                          {/* tax is stored flat on the product now — a name and
                              a percentage, with no tax-master row behind it */}
                          {(p.taxName || p.taxPct != null) ? (
                            <span style={{
                              background: "#fefce8", color: "#a16207",
                              fontSize: 11, fontWeight: 700,
                              padding: "3px 10px", borderRadius: 20,
                              border: "1px solid #fde68a", whiteSpace: "nowrap",
                            }}>
                              {p.taxName || "—"}
                              {p.taxPct != null && ` (${p.taxPct}%)`}
                            </span>
                          ) : (
                            <span style={{ color: "var(--mly-text-muted)", fontSize: 12 }}>—</span>
                          )}
                        </td>

                        {/* Millable */}
                        <td>
                          <Badge
                            active={p.isMillable}
                            activeLabel="Yes" inactiveLabel="No"
                            activeColor={{ bg: "#f0fdf4", text: "#16a34a", border: "#bbf7d0" }}
                          />
                        </td>

                        {/* Roastable */}
                        <td>
                          <Badge
                            active={p.isRoastable}
                            activeLabel="Yes" inactiveLabel="No"
                            activeColor={{ bg: "#fff7ed", text: "#ea580c", border: "#fed7aa" }}
                          />
                        </td>

                        {/* Max Offer */}
                        <td>
                          {p.maxOfferPct > 0 ? (
                            <span style={{
                              background: "var(--mly-accent-soft)", color: BRAND.forest,
                              fontSize: 11, fontWeight: 700,
                              padding: "3px 10px", borderRadius: 20,
                              border: "1px solid var(--mly-accent)",
                            }}>
                              {p.maxOfferPct}%
                            </span>
                          ) : (
                            <span style={{ color: "var(--mly-text-muted)", fontSize: 12 }}>—</span>
                          )}
                        </td>

                        {/* Status toggle */}
                        <td>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={!!p.isActive}
                              onChange={() => handleToggleStatus(p.id)}
                            />
                            <span className="slider round" />
                          </label>
                        </td>

                        {/* Best Selling toggle */}
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={!!p.isBestseller}
                                onChange={() => handleToggleBestSelling(p.id)}
                              />
                              <span className="slider round" />
                            </label>
                          </div>
                        </td>

                        {/* Popular Product toggle */}
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={!!p.isPopular}
                                onChange={() => handleTogglePopular(p.id)}
                              />
                              <span className="slider round" />
                            </label>
                          </div>
                        </td>

                        <td>{p.createdBy ?? "-"}</td>
                        <td>{formatDate(p.createdAt)}</td>
                        <td>{p.updatedBy ?? "-"}</td>
                        <td>{formatDate(p.updatedAt)}</td>

                        {/* Actions */}
                        <td>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              onClick={() => navigate(`/admin/products/view/${p.id}`)}
                              title="View"
                              style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "#f0fdf4", color: "#16a34a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                              onMouseEnter={(e) => e.currentTarget.style.background = "#dcfce7"}
                              onMouseLeave={(e) => e.currentTarget.style.background = "#f0fdf4"}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 4.6c5 0 9 3.6 10.5 7a1 1 0 0 1 0 .8C21 15.8 17 19.4 12 19.4S3 15.8 1.5 12.4a1 1 0 0 1 0-.8C3 8.2 7 4.6 12 4.6Zm0 3.3a4.1 4.1 0 1 0 0 8.2 4.1 4.1 0 0 0 0-8.2Zm0 2.2a1.9 1.9 0 1 1 0 3.8 1.9 1.9 0 0 1 0-3.8Z" /></svg>
                            </button>
                            <button
                              onClick={() => navigate(`/admin/products/edit/${p.id}`)}
                              title="Edit"
                              style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "var(--mly-accent-soft)", color: BRAND.forest, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                              onMouseEnter={(e) => e.currentTarget.style.background = "var(--mly-surface-3)"}
                              onMouseLeave={(e) => e.currentTarget.style.background = "var(--mly-accent-soft)"}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m16.6 2.9 4.5 4.5-11 11a1.6 1.6 0 0 1-.8.4l-5 1a1 1 0 0 1-1.2-1.2l1-5a1.6 1.6 0 0 1 .4-.8Zm5.9 3.1-4.5-4.5.8-.8a2.2 2.2 0 0 1 3.1 0l1.4 1.4a2.2 2.2 0 0 1 0 3.1Z" /></svg>
                            </button>
                            <button
                              onClick={() => handleDelete(p.id)}
                              title="Delete"
                              style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "#fff5f5", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                              onMouseEnter={(e) => e.currentTarget.style.background = "#fee2e2"}
                              onMouseLeave={(e) => e.currentTarget.style.background = "#fff5f5"}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9.6 2.2h4.8a1.6 1.6 0 0 1 1.6 1.6v1.4h4.6a1 1 0 0 1 0 2h-.5l-1 12.4a2.4 2.4 0 0 1-2.4 2.2H7.3a2.4 2.4 0 0 1-2.4-2.2l-1-12.4h-.5a1 1 0 0 1 0-2h4.6V3.8a1.6 1.6 0 0 1 1.6-1.6Zm.4 3h4V4.2h-4Zm-.1 5a.9.9 0 0 0-.9.9v6a.9.9 0 0 0 1.8 0v-6a.9.9 0 0 0-.9-.9Zm4.2 0a.9.9 0 0 0-.9.9v6a.9.9 0 0 0 1.8 0v-6a.9.9 0 0 0-.9-.9Z" /></svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {!loading && booted && rows.length === 0 && (
                <div style={{ textAlign: "center", padding: 40, color: "var(--mly-text-muted)" }}>
                  {search
                    ? `No products match “${search}”`
                    : "No products found"}
                </div>
              )}
            </div>

            {/* ── server pagination ── */}
            {totalPages > 1 && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 6, flexWrap: "wrap", marginTop: 18,
              }}>
                <button
                  type="button"
                  style={pageBtn(false, page <= 1)}
                  disabled={page <= 1}
                  onClick={() => goToPage(1)}
                >
                  «
                </button>
                <button
                  type="button"
                  style={pageBtn(false, page <= 1)}
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                >
                  Prev
                </button>

                {pageWindow[0] > 1 && <span style={{ color: BRAND.stone, fontSize: 12 }}>…</span>}

                {pageWindow.map((n) => (
                  <button
                    key={n}
                    type="button"
                    style={pageBtn(n === page, false)}
                    onClick={() => goToPage(n)}
                  >
                    {n}
                  </button>
                ))}

                {pageWindow[pageWindow.length - 1] < totalPages && (
                  <span style={{ color: BRAND.stone, fontSize: 12 }}>…</span>
                )}

                <button
                  type="button"
                  style={pageBtn(false, page >= totalPages)}
                  disabled={page >= totalPages}
                  onClick={() => goToPage(page + 1)}
                >
                  Next
                </button>
                <button
                  type="button"
                  style={pageBtn(false, page >= totalPages)}
                  disabled={page >= totalPages}
                  onClick={() => goToPage(totalPages)}
                >
                  »
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Import Modal ── */}
      {showImportModal && (
        <div className="import-modal-overlay" onClick={() => !importing && setShowImportModal(false)}>
          <div className="import-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: 20 }}>Import Products from Excel</h3>

            {excelData && (
              <div style={{ marginBottom: 20 }}>
                <p><strong>Found {excelData.length} records</strong> to import</p>
                <div style={{ maxHeight: 300, overflowY: "auto", border: "1px solid var(--mly-border)", borderRadius: 8, padding: 10 }}>
                  <table style={{ width: "100%", fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th>Si No</th>
                        <th>Name</th>
                        <th>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excelData.slice(0, 10).map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{row.name     || row.Name     || "-"}</td>
                          <td>{row.category || row.Category || "-"}</td>
                        </tr>
                      ))}
                      {excelData.length > 10 && (
                        <tr>
                          <td colSpan="3" style={{ textAlign: "center", color: "var(--mly-text-muted)" }}>
                            ... and {excelData.length - 10} more records
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {importing && (
              <div style={{ marginBottom: 20 }}>
                <div className="progress-bar-wrap">
                  <div className="progress-fill" style={{ width: `${importProgress}%` }} />
                </div>
                <p style={{ marginTop: 10, fontSize: 12, textAlign: "center" }}>
                  Importing... {Math.round(importProgress)}%
                </p>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowImportModal(false)}
                disabled={importing}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleImport}
                disabled={importing}
              >
                {importing ? "Importing..." : "Import Products"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IteamMaster;
